/**
 * Hotel Supplier Management feature routes — a TOP-LEVEL module (peer of Chat /
 * CRM / Expense), promoted out of the CRM subtree in the 2026-07 IA change.
 *
 * Exposed as an Expense-style pill-tab bar (HotelSupplierLayout →
 * CrmModuleShell) inside the shared shell (.crm-scope + MeProvider +
 * capability-gated tabs), the same shell /crm uses.
 *
 * Customers and Agreements are new pages owned by this module
 * (features/hotel-supplier/pages/*). Hotels and Rate Opportunities still
 * physically live under features/crm/pages/sourcing and resolve via the
 * `@/features/crm/*` alias — relocating them here is a separate cleanup, not a
 * behaviour change.
 *
 * Airlines and Car Rental (2026-10-08) are the non-hotel vendor master: the
 * vendors Vendor Relations keeps contacts and contracts on so AirPortal can be
 * retired. Sourced from Andavo's own vendor table, never AirPortal.
 *
 * The old Sourcing and Renewals routes are GONE: they are stages inside
 * Agreements now (?stage=sourcing / ?stage=renewal). Their page components under
 * features/crm/pages/sourcing/{workspace,renewals} are consequently orphaned —
 * deleting them is a follow-up, kept for one release as a fallback.
 */
import { Navigate, Route } from 'react-router';
import type { FeatureRouteList } from '@/features/routes-registry';
import { HotelSupplierLayout } from './HotelSupplierLayout';
import SupplierCustomersPage from './pages/customers/page';
import AgreementsPage from './pages/agreements/page';
import NewAgreementPage from './pages/agreements/new-page';
import AgreementDetailPage from './pages/agreements/detail-page';
import ImportAgreementsPage from './pages/agreements/import-page';
import HotelsPage from '@/features/crm/pages/sourcing/hotels/page';
import RateOpportunitiesPage from '@/features/crm/pages/sourcing/rate-opportunities/page';
import VendorsPage from './pages/vendors/VendorsPage';
import VendorDetailPage from './pages/vendors/VendorDetailPage';

export const hotelSupplierRoutes: FeatureRouteList = [
  <Route
    element={<HotelSupplierLayout />}
    key="hotel-supplier"
    path="hotel-supplier"
  >
    {/* Root redirects to Customers — the workflow starts by picking a customer. */}
    <Route
      element={<Navigate replace to="/hotel-supplier/customers" />}
      index
      key="hsm-index"
    />
    <Route
      element={<SupplierCustomersPage />}
      key="hsm-customers"
      path="customers"
    />
    {/* The tab was "Clients" until the internal naming convention settled on
        "Customer" (2026-08-01). Redirect rather than drop the old path: it was
        live in staging and anyone who bookmarked or shared it would otherwise
        land on a blank route with no clue why. The literal "clients" below is
        the OLD url and must stay spelled that way — that is the whole point. */}
    <Route
      element={<Navigate replace to="/hotel-supplier/customers" />}
      key="hsm-clients-legacy"
      path="clients"
    />
    <Route element={<HotelsPage />} key="hsm-hotels" path="hotels" />
    <Route
      element={<NewAgreementPage />}
      key="hsm-agreement-new"
      path="agreements/new"
    />
    {/* BEFORE `agreements/:id` in the file, though react-router ranks static
        segments above dynamic ones regardless — an `/agreements/import` that
        resolved to the detail page would load an agreement with the id
        "import" and render a not-found. Kept adjacent so the pair is read
        together. */}
    <Route
      element={<ImportAgreementsPage />}
      key="hsm-agreement-import"
      path="agreements/import"
    />
    <Route
      element={<AgreementDetailPage />}
      key="hsm-agreement-detail"
      path="agreements/:id"
    />
    <Route
      element={<AgreementsPage />}
      key="hsm-agreements"
      path="agreements"
    />
    <Route
      element={<RateOpportunitiesPage />}
      key="hsm-rate-opportunities"
      path="rate-opportunities"
    />
    {/* Airline + car rental vendor master (2026-10-08). Same components for
        both, keyed on the service type; `key` on the element forces a fresh
        mount when moving between the two tabs, so one tab's search and page
        never leak into the other. */}
    <Route
      element={<VendorsPage key="air" type="air" />}
      key="hsm-airlines"
      path="airlines"
    />
    <Route
      element={<VendorDetailPage key="air" type="air" />}
      key="hsm-airline-detail"
      path="airlines/:uid"
    />
    <Route
      element={<VendorsPage key="car" type="car" />}
      key="hsm-car-rental"
      path="car-rental"
    />
    <Route
      element={<VendorDetailPage key="car" type="car" />}
      key="hsm-car-rental-detail"
      path="car-rental/:uid"
    />
  </Route>,
];
